import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type CreatePartyContactRequest,
  type PartyContact,
  type UpdatePartyContactRequest,
} from '../client';

/**
 * Stable query-key factory for party-contact-scoped caches. Keyed by
 * `partyId` so each party's contact list has its own cache slot — toggling
 * between parties in different tabs doesn't share state.
 */
export const partyContactKeys = {
  all: ['directory', 'partyContacts'] as const,
  byParty: (partyId: string) => ['directory', 'partyContacts', 'party', partyId] as const,
  byId: (id: string) => ['directory', 'partyContacts', 'id', id] as const,
};

/**
 * Fetch every contact for a party. Pass `null` to keep the query disabled
 * (e.g. while the route param is still resolving, or in create-mode of a
 * Party Details page where there's no party id yet).
 */
export function usePartyContacts(
  partyId: string | null,
  options?: Omit<UseQueryOptions<PartyContact[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<PartyContact[], Error>({
    queryKey: partyId ? partyContactKeys.byParty(partyId) : partyContactKeys.all,
    queryFn: () => directoryApi.getPartyContacts(partyId!),
    enabled: !!partyId,
    staleTime: 60 * 1000,
    ...options,
  });
}

/**
 * Create a contact. Invalidates the parent party's contact list so the
 * new row shows up immediately.
 */
export function useCreatePartyContact() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreatePartyContactRequest>({
    mutationFn: (body) => directoryApi.createPartyContact(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({ queryKey: partyContactKeys.byParty(vars.partyId) });
    },
  });
}

/**
 * Update a contact. Caller passes `partyId` alongside the body so cache
 * invalidation can target the right party-list cache without round-tripping
 * back to the BE just to discover the parent.
 */
export function useUpdatePartyContact() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { body: UpdatePartyContactRequest; partyId: string }
  >({
    mutationFn: ({ body }) => directoryApi.updatePartyContact(body),
    onSuccess: (_id, { body, partyId }) => {
      qc.invalidateQueries({ queryKey: partyContactKeys.byParty(partyId) });
      qc.invalidateQueries({ queryKey: partyContactKeys.byId(body.contactId) });
    },
  });
}

/**
 * Soft-delete a contact. Caller passes `partyId` for the same reason as
 * update — the deleted row's payload isn't available post-delete so we
 * can't read it back to find the parent.
 */
export function useDeletePartyContact() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; partyId: string }>({
    mutationFn: ({ id }) => directoryApi.deletePartyContact(id),
    onSuccess: (_, { id, partyId }) => {
      qc.invalidateQueries({ queryKey: partyContactKeys.byParty(partyId) });
      qc.removeQueries({ queryKey: partyContactKeys.byId(id) });
    },
  });
}
